/* eslint-disable import/no-dynamic-require */
import { Amplify } from 'aws-amplify';
import queryMiddleware from 'farce/queryMiddleware';
import createFarceRouter from 'found/createFarceRouter';
import createRender from 'found/createRender';
import { Resolver } from 'found-relay';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { Button } from 'reactstrap';

import ErrorBoundary from 'src/components/ErrorBoundary';
import HTTP404 from 'src/components/HTTP404';
import { AmplifyConfig } from 'src/config';
import routes from 'src/routes';
import environment from 'src/util/environment';
import browserProtocol from 'src/util/history';
import 'src/scss/index.scss';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'react-bootstrap-typeahead/css/Typeahead.bs5.css';
import { I18nProvider } from 'src/util/i18n/I18nProvider';
import { installCaptureApi, isCaptureModeEnabled } from 'src/util/i18n/capture';
import { initializeIntlCurrency } from 'src/util/i18n/currency';
import {
  DEFAULT_LOCALE, applyI18nConfig, buildI18nConfig, getConfiguredFullLocale,
  initializeI18n, loadI18nConfig,
} from 'src/util/i18n/handler';
import localeHistoryMiddleware from 'src/util/i18n/localeHistoryMiddleware';
import { canonicalizeInitialLocaleUrl } from 'src/util/i18n/localeUrl';
import { intercomHandler } from 'src/util/intercom';
import logger from 'src/util/logger';

const buildRouter = () => createFarceRouter({
  historyProtocol: browserProtocol,
  historyMiddlewares: [localeHistoryMiddleware, queryMiddleware],
  routeConfig: routes,

  /* eslint-disable react/prop-types */
  render: createRender({
    renderError: ({ error }) => {
      logger.log({ error });
      return error.status === 404 ? <HTTP404 /> : <div>{`Error: ${error}`}</div>;
    },
  }),
  /* eslint-enable react/prop-types */
});

/**
 * Mount the application, including the router and i18n provider, to the DOM.
 * @param {object} i18nConfig - The i18n configuration to use for the initial render.
 * @returns {void}
 */
const mountApplication = (i18nConfig) => {
  const Router = buildRouter();
  const rootContainer = createRoot(document.getElementById('container'));
  rootContainer.render(
    (
      <I18nProvider initialConfig={i18nConfig}>
        <HelmetProvider>
          <ErrorBoundary
            fallback={(retry) => (
              <Button onClick={retry} type="button">
                Retry
              </Button>
            )}
          >
            <Router resolver={new Resolver(environment)} />
          </ErrorBoundary>
        </HelmetProvider>
      </I18nProvider>
    ),
  );
  intercomHandler();
};

/**
 * Initializes services and returns the locale configuration used for the first render.
 * @returns {Promise<object>} The i18n configuration used for the first render.
 */
const initializeApplication = async () => {
  try {
    // AmplifyConfig() can be undefined before deployment config is injected.
    const amplifyConfig = AmplifyConfig();
    if (amplifyConfig) {
      Amplify.configure(amplifyConfig);
    }

    const [i18nConfig, currencyConfig] = await Promise.all([
      initializeI18n(),
      loadI18nConfig(getConfiguredFullLocale()),
    ]);
    initializeIntlCurrency(currencyConfig);
    return i18nConfig;
  } catch (error) {
    logger.error('Application i18n bootstrap fallback', error);
    const fallbackConfig = buildI18nConfig(DEFAULT_LOCALE);
    applyI18nConfig(fallbackConfig);
    initializeIntlCurrency(fallbackConfig);
    return fallbackConfig;
  }
};

/**
 * Bootstraps the application by initializing services and mounting the application to the DOM.
 * @returns {Promise<void>} A promise that resolves when the application is mounted.
 * @throws {Error} If the application fails to mount.
 */
const bootstrap = async () => {
  if (isCaptureModeEnabled()) {
    installCaptureApi();
  }
  const i18nConfig = await initializeApplication();
  canonicalizeInitialLocaleUrl(i18nConfig.fullLocale);
  mountApplication(i18nConfig);
};

bootstrap().catch((error) => logger.error('Application mount failed', error));
